import React, { useState } from 'react'
import { X, Send } from 'lucide-react'

interface MessageModalProps {
    isOpen: boolean
    onClose: () => void
    onSend: (message: string) => Promise<void>
    recipientName: string
    context: string // e.g., "Regarding your application for Frontend Developer"
    isLoading?: boolean
}

const MessageModal: React.FC<MessageModalProps> = ({
    isOpen,
    onClose,
    onSend,
    recipientName,
    context,
    isLoading = false
}) => {
    const [message, setMessage] = useState('')
    const [isSending, setIsSending] = useState(false)

    const handleSend = async () => {
        if (!message.trim() || isSending) return

        setIsSending(true)
        try {
            await onSend(message.trim())
            setMessage('')
            onClose()
        } catch (error) {
            console.error('Failed to send message:', error)
        } finally {
            setIsSending(false)
        }
    }

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
            e.preventDefault()
            if (message.trim()) {
                handleSend()
            }
        }
    }

    if (!isOpen) return null

    return (
        <div className="fixed inset-0 bg-black/50 dark:bg-black/70 flex items-center justify-center z-50">
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg max-w-lg w-full mx-4 border border-gray-200 dark:border-gray-700 overflow-hidden">
                {/* Header with gradient - Theme-aware */}
                <div className="flex items-center justify-between px-4 py-3 bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-700 dark:to-gray-800 border-b border-gray-200 dark:border-gray-700">
                    <div>
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                            Send Message to {recipientName}
                        </h3>
                        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{context}</p>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-1 rounded hover:opacity-80 transition-opacity text-gray-600 dark:text-gray-400"
                        aria-label="Close message"
                    >
                        <X size={20} />
                    </button>
                </div>

                {/* Message input area */}
                <div className="p-4 bg-white dark:bg-gray-800">
                    <textarea
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        onKeyDown={handleKeyDown}
                        placeholder="Type your message here..."
                        className="w-full h-32 p-3 border border-gray-300 dark:border-gray-600 rounded-lg resize-none focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:focus:ring-blue-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 transition-colors"
                        disabled={isSending || isLoading}
                        autoFocus
                    />
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                        Press Ctrl+Enter to send quickly
                    </p>
                </div>

                {/* Footer with buttons */}
                <div className="flex items-center justify-end gap-3 p-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50">
                    <button
                        onClick={onClose}
                        disabled={isSending || isLoading}
                        className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleSend}
                        disabled={!message.trim() || isSending || isLoading}
                        className="flex items-center gap-2 px-4 py-2 bg-primary-600 hover:bg-primary-700 disabled:bg-gray-400 dark:disabled:bg-gray-600 text-white rounded-lg disabled:cursor-not-allowed transition-colors font-medium"
                    >
                        {isSending ? (
                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        ) : (
                            <Send size={16} />
                        )}
                        Send Message
                    </button>
                </div>
            </div>
        </div>
    )
}

export default MessageModal